'use client';

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Building2, Globe, Check, Loader2 } from 'lucide-react';
import type { ReactElement } from 'react';
import { WorkOSSSOButton } from './workos-sso-button';

interface Organization {
  id: string;
  name: string;
  domains: Array<{
    object: string;
    id: string;
    domain: string;
    organizationId: string;
    state: string;
    verificationStrategy: string;
    createdAt: string;
    updatedAt: string;
  }>;
  object: string;
}

interface WorkOSOrganizationSelectorProps {
  email: string;
  onOrganizationSelect: (organizationId: string) => void;
  className?: string;
}

export function WorkOSOrganizationSelector({
  email,
  onOrganizationSelect,
  className = '',
}: WorkOSOrganizationSelectorProps): ReactElement {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedOrg, setSelectedOrg] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrganizations = async (): Promise<void> => {
      try {
        setIsLoading(true);
        
        const domain = email.split('@')[1]?.toLowerCase();
        if (!domain) {
          setIsLoading(false);
          return;
        }

        const response = await fetch(`/api/auth/organizations?domain=${encodeURIComponent(domain)}`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch organizations');
        }

        const data = await response.json();
        setOrganizations(data.organizations ?? []);
        
        // Auto-select if only one organization
        if (data.organizations?.length === 1) {
          setSelectedOrg(data.organizations[0]?.id ?? null);
        }
      } catch (err) {
        console.error('Failed to fetch organizations:', err);
      } finally {
        setIsLoading(false);
      }
    };

    if (email) {
      void fetchOrganizations();
    }
  }, [email]);

  if (isLoading) {
    return (
      <Card className={`${className} w-full`}>
        <CardContent className="flex items-center justify-center py-8 min-h-[160px]">
          <div className="text-center w-full">
            <Loader2 className="size-6 animate-spin mx-auto mb-2" />
            <span className="block">Finding your organization...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (organizations.length === 0) {
    return (
      <Card className={`${className} w-full`}>
        <CardContent className="py-6 min-h-[160px]">
          <div className="text-center w-full">
            <Building2 className="size-12 mx-auto text-muted-foreground mb-3" />
            <p className="font-medium">No organization found</p>
            <p className="text-sm text-muted-foreground mt-1">
              We couldn&apos;t find an organization for your email domain.
            </p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => onOrganizationSelect('new')}
            >
              Request Organization Access
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`${className} w-full`}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="size-5" />
          Select Your Organization
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Choose the organization you&apos;d like to sign in to:
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {organizations.map((org) => (
          <div
            key={org.id}
            className={`p-4 border rounded-lg cursor-pointer transition-all ${
              selectedOrg === org.id
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/20'
                : 'border-border hover:border-border/60'
            }`}
            onClick={() => setSelectedOrg(org.id)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="size-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 text-white grid place-items-center">
                  <Building2 className="size-5" />
                </div>
                <div>
                  <p className="font-medium">{org.name}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {org.domains.map((domain) => (
                      <Badge key={domain.id} variant="outline" className="text-xs">
                        <Globe className="size-3 mr-1" />
                        {domain.domain}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
              {selectedOrg === org.id && (
                <Check className="size-5 text-blue-600" />
              )}
            </div>
          </div>
        ))}
        
        <WorkOSSSOButton 
          organizationId={selectedOrg ?? ''}
          variant="default"
          size="default"
        />
      </CardContent>
    </Card>
  );
}
